///
/// EarthBrowser
///
/// Created by Matt Giger on 2/2/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { type IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

export class GLTFGeometry implements Geometry {
	public POSITION?: VertexAttribute
	public NORMAL?: VertexAttribute
	public TEXCOORD_0?: VertexAttribute
	public TANGENT?: VertexAttribute
	public COLOR_0?: VertexAttribute
	public JOINTS_0?: VertexAttribute
	public WEIGHTS_0?: VertexAttribute

	public mode: DrawMode = DrawMode.Triangles
	public vertexBuffer: VertexBuffer
	public indexBuffer?: IndexBuffer
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true
	private context: StovokorContext

	constructor(context: StovokorContext, mode: DrawMode, attributes: Map<string, VertexAttribute>, indexBuffer?: IndexBuffer) {
		this.context = context
		this.mode = mode
		this.indexBuffer = indexBuffer

		const attrs: VertexAttribute[] = []

		const POSITION = attributes.get("POSITION")
		if(POSITION) {
			this.POSITION = POSITION
			attrs.push(POSITION)
			POSITION.calculateBounds()
			this.bounds = POSITION.bounds
			this.boundingSphere = this.bounds.sphere
		} else {
			throw new Error("No position attribute found")
		}

		const NORMAL = attributes.get("NORMAL")
		if(NORMAL) {
			this.NORMAL = NORMAL
			attrs.push(NORMAL)
		}

		const TEXCOORD_0 = attributes.get("TEXCOORD_0")
		if(TEXCOORD_0) {
			this.TEXCOORD_0 = TEXCOORD_0
			attrs.push(TEXCOORD_0)
		}

		const TANGENT = attributes.get("TANGENT")
		if(TANGENT) {
			this.TANGENT = TANGENT
			attrs.push(TANGENT)
		}

		const COLOR_0 = attributes.get("COLOR_0")
		if(COLOR_0) {
			this.COLOR_0 = COLOR_0
			attrs.push(COLOR_0)
		}

		const JOINTS_0 = attributes.get("JOINTS_0")
		if(JOINTS_0) {
			this.JOINTS_0 = JOINTS_0
			attrs.push(JOINTS_0)
		}

		const WEIGHTS_0 = attributes.get("WEIGHTS_0")
		if(WEIGHTS_0) {
			this.WEIGHTS_0 = WEIGHTS_0
			attrs.push(WEIGHTS_0)
		}

		this.vertexBuffer = new VertexBuffer(this.context, attrs)
	}

	/**
	 * Shader defines for the optional attributes present. POSITION, NORMAL
	 * and TEXCOORD_0 are declared unconditionally in gltf.glsl and must not
	 * appear here: defines are emitted into the GLSL, so `#define POSITION 1`
	 * would rewrite the attribute declaration itself
	 */
	public requiredAttributes(): Record<string, string> {
		const defines: Record<string, string> = {}
		if(this.TANGENT) {
			defines.USE_TANGENT = "1"
		}
		if(this.COLOR_0) {
			defines.USE_COLOR_0 = "1"
		}
		if(this.JOINTS_0) {
			defines.USE_JOINTS = "1"
		}
		if(this.WEIGHTS_0) {
			defines.USE_JOINTS = "1"
		}
		return defines
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer?.destroy()
	}
}
